// ADR-002: the embed is a single custom element registered by a script tag, all markup inside a
// Shadow DOM root so styles are isolated in both directions. This file is the CTA + overlay shell;
// question-flow.ts and result-screen.ts render inside it. Without a `config-endpoint` attribute,
// runs against mock-data.ts (see docs/plans/2026-08-26-presentation-layer.md); with one, runs the
// real Session API per docs/design/2026-08-26-session-api-contract.md (see
// docs/plans/2026-08-26-presentation-session-api-wiring.md).

import { tokenStylesheet } from "./tokens";
import { componentStylesheet } from "./component-styles";
import { QuestionFlow, type SessionApiBundle } from "./question-flow";
import { ResultScreen, renderOptOutScreen } from "./result-screen";
import { loadMockConfig } from "./mock-data";
import * as apiClient from "./api-client";
import { getTurnstileToken } from "./turnstile";
import { hostImageUrl } from "./assets";
import { loadingHtml } from "./loading";
import type { ConfigVersion } from "../types";
import type { DiscoverySessionState } from "../session/state";
import { resolveCampaign } from "../campaign";

const RESUME_STORAGE_KEY = "bfc-discovery-session-id";

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export class BfcDiscoveryElement extends HTMLElement {
  private root: ShadowRoot;
  private config: ConfigVersion | null = null;
  private overlay: HTMLElement | null = null;
  private lastFocused: HTMLElement | null = null;
  private keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
  /** True once the result screen has shown something actionable and the visitor hasn't yet taken
   * one of its CTAs (Book Now, Grab Report, Find a Board Member) — guards close() behind a
   * confirmation step while true. Set/cleared by ResultScreenHost callbacks in showResult(). */
  private resultActionPending = false;
  /** Set by showCloseConfirm() while the confirm step has replaced the result screen's content —
   * the DOM nodes it swapped out, restored by dismissCloseConfirm(). */
  private savedPanelContent: DocumentFragment | null = null;
  /** Aborts the in-flight session start (today: the Turnstile widget) when the panel closes or the
   * flow restarts, so a challenge widget never outlives the panel that asked for it. */
  private startAbort: AbortController | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    const style = document.createElement("style");
    style.textContent = tokenStylesheet + componentStylesheet;
    this.root.appendChild(style);

    const endpoint = this.getAttribute("config-endpoint");
    if (!endpoint) {
      // Mock preview path: no kill switch exists to check, render immediately, unchanged.
      this.renderCta();
      return;
    }

    // UC-10/ADR-003: the service-side kill switch. GET /config 503s while disabled (routes.ts),
    // so this is "config fetch fails: render no CTA, silent, fail closed" (ADR-008) applied to the
    // CTA itself, not just the overlay it opens — without this check the CTA button always
    // rendered and only failed once clicked, which is the flag not actually removing anything the
    // embed shows, contrary to ADR-003's "anything our embed renders disappears".
    void apiClient
      .fetchConfig(endpoint)
      .then(() => this.renderCta())
      .catch(() => {
        // Disabled, no config published yet, or a transport error: no CTA, no error shown.
      });
  }

  private renderCta(): void {
    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "bfc-btn bfc-cta";
    cta.textContent = this.getAttribute("cta-label") ?? "Find My Board Member";
    cta.addEventListener("click", () => this.open(cta));
    this.root.appendChild(cta);
  }

  private open(origin: HTMLElement): void {
    this.lastFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement("div");
    overlay.className = "bfc-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const panel = document.createElement("div");
    panel.className = "bfc-panel";

    const close = document.createElement("button");
    close.type = "button";
    close.className = "bfc-close";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    close.addEventListener("click", () => this.close());
    panel.appendChild(close);

    const body = document.createElement("div");
    body.className = "bfc-flow-mount";
    panel.appendChild(body);

    overlay.appendChild(panel);
    overlay.addEventListener("click", (e) => {
      if (e.target !== overlay) return;
      // A backdrop click while the confirm step is already up just dismisses it (same as "Keep it
      // open"), rather than re-triggering close() and reshowing the same prompt.
      if (panel.querySelector(".bfc-close-confirm")) {
        this.dismissCloseConfirm();
        return;
      }
      this.close();
    });

    this.root.appendChild(overlay);
    this.overlay = overlay;
    document.addEventListener("keydown", this.keydownHandler);

    this.animateOpen(panel, origin);
    this.showIntro(panel, body);

    requestAnimationFrame(() => {
      const first = panel.querySelector<HTMLElement>("[data-autofocus]") ?? panel.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    });
  }

  /** The first thing a visitor sees after the CTA: a short welcome before any question. No session
   * exists yet (and no Turnstile check or POST /session has run), so a visitor who bounces here
   * costs nothing and leaves no abandoned-session record in telemetry. "I'm ready" is what starts
   * the real flow. Copy is from Sarah's style suggestions and awaits the same voice review as the
   * rest of the visitor-facing text. */
  private showIntro(panel: HTMLElement, container: HTMLElement): void {
    panel.classList.add("bfc-panel--intro");
    const photo = hostImageUrl ? `<img class="bfc-intro-photo" src="${hostImageUrl}" alt="" />` : "";
    container.innerHTML = `
      <div class="bfc-intro bfc-fade-enter">
        ${photo ? `<div class="bfc-intro-hero">${photo}</div>` : ""}
        <div class="bfc-intro-body">
          <h2 class="bfc-intro-title">Let's have a chat.</h2>
          <p class="bfc-intro-copy">Just a few quick questions. No wrong answers, whether something's broken, something's working but could be better, or you're just figuring out what's next.</p>
          <button type="button" class="bfc-btn bfc-start" data-autofocus>I'm ready <span aria-hidden="true">&rarr;</span></button>
        </div>
      </div>
    `;
    const img = container.querySelector<HTMLImageElement>(".bfc-intro-photo");
    img?.addEventListener("error", () => img.closest(".bfc-intro-hero")?.remove());
    container.querySelector(".bfc-start")?.addEventListener("click", () => {
      panel.classList.remove("bfc-panel--intro");
      void this.startFlow(container);
    });
  }

  /** With no `config-endpoint` attribute, runs the disposable mock preview path unchanged
   * (loadMockConfig, no network). With it set, this is real: GET /config, a Turnstile token, then
   * POST /session, per design/2026-08-26-session-api-contract.md — errors at any of those steps
   * render a fail-closed message rather than dropping the visitor into a broken flow. */
  private async startFlow(container: HTMLElement, restarted = false): Promise<void> {
    const endpoint = this.getAttribute("config-endpoint");
    if (!endpoint) {
      const config = this.getMockConfig();
      this.mountQuestionFlow(container, config);
      return;
    }

    container.innerHTML = `<div class="bfc-fade-enter">${loadingHtml({ label: "Loading", heading: "Just a moment." })}</div>`;
    this.startAbort?.abort();
    const abort = new AbortController();
    this.startAbort = abort;
    try {
      const config = await apiClient.fetchConfig(endpoint);
      const siteKey = this.getAttribute("turnstile-site-key") ?? undefined;
      const turnstileToken = await getTurnstileToken({
        siteKey,
        signal: abort.signal,
        // Turnstile shows its checkbox at the bottom of the screen, outside the panel (see
        // turnstile.ts), so the panel says where to look rather than leaving "Just a moment." up.
        onInteractive: () => {
          container.innerHTML = `<div class="bfc-fade-enter">${loadingHtml({
            label: "Quick check",
            heading: "One quick check.",
            note: "Please tick the box at the bottom of your screen so we know you're not a bot.",
          })}</div>`;
        },
      });
      const resumeSessionId = localStorage.getItem(RESUME_STORAGE_KEY) ?? undefined;
      const started = await apiClient.startSession(endpoint, config.versionId, turnstileToken, resumeSessionId, restarted, this.pagePath());
      localStorage.setItem(RESUME_STORAGE_KEY, started.sessionId);
      this.mountQuestionFlow(container, config, {
        baseUrl: endpoint,
        sessionId: started.sessionId,
        resumedAnswers: started.answers,
        nextQuestionId: started.nextQuestionId,
        questionOptions: started.questionOptions,
        campaign: started.campaign,
      });
    } catch {
      container.innerHTML = `
        <div class="bfc-waiting bfc-fade-enter">
          <p class="bfc-alert bfc-alert--error">Something went wrong loading this. Please try again in a moment.</p>
        </div>
      `;
    }
  }

  /** The host page's path, matched against the Campaigns tab (docs/plans/2026-10-13-campaigns.md).
   * A `page-path` attribute overrides it, so the preview harness can stand in for a campaign page
   * it isn't served from. Nothing is gained by setting it on a real page: a visitor can open the
   * campaign page itself just as easily. */
  private pagePath(): string {
    return this.getAttribute("page-path") ?? window.location.pathname;
  }

  private getMockConfig(): ConfigVersion {
    if (!this.config) this.config = loadMockConfig();
    return this.config;
  }

  private mountQuestionFlow(container: HTMLElement, config: ConfigVersion, bundle?: SessionApiBundle): void {
    const flow = new QuestionFlow(
      container,
      config,
      {
        onComplete: (session) => this.showResult(container, config, session, bundle),
        onOptOut: () => this.showOptOut(container, config),
      },
      bundle
    );
    flow.mount();
  }

  private showResult(container: HTMLElement, config: ConfigVersion, session: DiscoverySessionState, bundle?: SessionApiBundle): void {
    // Live mode takes whatever the Worker resolved at session start. Mock mode has no Worker, so
    // resolves the same way locally against the mock config's campaigns.
    const campaign = bundle ? bundle.campaign : resolveCampaign(config.campaigns, this.pagePath(), new Date());
    const screen = new ResultScreen(
      container,
      config,
      session,
      {
        onRestart: () => this.restart(container),
        onResultShown: () => (this.resultActionPending = true),
        onResultActionTaken: () => (this.resultActionPending = false),
      },
      bundle,
      campaign
    );
    void screen.mount();
  }

  /** "Got another problem?" Same rule as closing the panel: while the match is still unactioned
   * (no Learn More or report click yet) the visitor gets the keep-it-open prompt first, with
   * "start over" as the way through instead of "close". Once they have acted there is nothing
   * left to lose, so it restarts immediately. The finished session is terminal server-side, so
   * the stored resume id just falls through to a fresh session (routes.ts handleStartSession). */
  private restart(container: HTMLElement): void {
    if (this.resultActionPending) {
      this.showCloseConfirm("restart", () => this.startOver(container));
      return;
    }
    this.startOver(container);
  }

  private startOver(container: HTMLElement): void {
    // The guard belongs to the result screen; the new session's questions have nothing to lose yet.
    this.resultActionPending = false;
    this.savedPanelContent = null;
    void this.startFlow(container, true);
  }

  private showOptOut(container: HTMLElement, config: ConfigVersion): void {
    renderOptOutScreen(container, config);
  }

  /** `force` skips the close-confirmation guard — used by the confirm step's own "Close anyway"
   * button, so it doesn't just re-trigger the same prompt. */
  private close(force = false): void {
    if (!this.overlay) return;
    if (!force && this.resultActionPending) {
      this.showCloseConfirm();
      return;
    }
    const panel = this.overlay.querySelector<HTMLElement>(".bfc-panel");
    document.removeEventListener("keydown", this.keydownHandler);
    this.startAbort?.abort();
    this.startAbort = null;

    const finish = () => {
      this.overlay?.remove();
      this.overlay = null;
      this.lastFocused?.focus();
    };

    if (!panel) {
      finish();
      return;
    }
    const duration = this.durationMs("--bfc-motion-duration-slow");
    const anim = panel.animate(
      [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: "scale(0.96)" },
      ],
      { duration, easing: this.easing(), fill: "forwards" }
    );
    anim.finished.then(finish).catch(finish);
  }

  /** Replaces the result screen's content with the confirmation, rather than appending it below
   * — stacking a "you'll lose this" prompt underneath the very match it's warning about read as
   * cluttered, and it's not obvious to a visitor which part of the panel they're meant to act on.
   * The replaced content's DOM nodes (not just their markup) are stashed in
   * `this.savedPanelContent` and moved back in by `dismissCloseConfirm`, so "Keep it open"
   * restores the exact same nodes — including their event listeners (the Book Now/Grab Report
   * click handlers that clear `resultActionPending`) — rather than re-rendering from scratch.
   * Still DOM inside the same overlay, so handleKeydown's focus-trap query (which walks
   * `this.overlay`) picks the confirm's buttons up automatically. No-ops if one is already
   * showing. */
  private showCloseConfirm(kind: "close" | "restart" = "close", proceed: () => void = () => this.close(true)): void {
    if (!this.overlay) return;
    const body = this.overlay.querySelector<HTMLElement>(".bfc-flow-mount");
    if (!body || body.querySelector(".bfc-close-confirm")) return;

    const saved = document.createDocumentFragment();
    while (body.firstChild) saved.appendChild(body.firstChild);
    this.savedPanelContent = saved;

    const confirm = document.createElement("div");
    confirm.className = "bfc-close-confirm bfc-fade-enter";
    confirm.innerHTML = `
      <p class="bfc-close-confirm-heading">Wait, don't lose this</p>
      <p class="bfc-close-confirm-body">${
        kind === "restart" ? "Starting over" : "Closing now"
      } means losing this match. Save the PDF recommendation or book your session first, or ${kind === "restart" ? "start over" : "close"} anyway.</p>
      <div class="bfc-close-confirm-actions">
        <button type="button" class="bfc-btn bfc-continue" data-action="stay">Keep it open</button>
        <button type="button" class="bfc-close-anyway" data-action="close">${kind === "restart" ? "Start over anyway" : "Close anyway"}</button>
      </div>
    `;
    body.appendChild(confirm);

    confirm.querySelector('[data-action="stay"]')?.addEventListener("click", () => this.dismissCloseConfirm());
    // "Close anyway" tears down the whole overlay next, so there's no point restoring the saved
    // content first. "Start over anyway" likewise replaces the mount's content outright.
    confirm.querySelector('[data-action="close"]')?.addEventListener("click", proceed);

    (confirm.querySelector<HTMLElement>('[data-action="stay"]'))?.focus();
  }

  /** Restores whatever `showCloseConfirm` replaced, putting the visitor right back where they
   * were rather than re-rendering the result screen from scratch. */
  private dismissCloseConfirm(): void {
    const body = this.overlay?.querySelector<HTMLElement>(".bfc-flow-mount");
    if (!body) return;
    body.querySelector(".bfc-close-confirm")?.remove();
    if (this.savedPanelContent) {
      body.appendChild(this.savedPanelContent);
      this.savedPanelContent = null;
    }
  }

  private animateOpen(panel: HTMLElement, origin: HTMLElement): void {
    const rect = origin.getBoundingClientRect();
    const panelRectEstimate = panel.getBoundingClientRect();
    const originX = rect.left + rect.width / 2 - (panelRectEstimate.left + panelRectEstimate.width / 2);
    const originY = rect.top + rect.height / 2 - (panelRectEstimate.top + panelRectEstimate.height / 2);

    const duration = this.durationMs("--bfc-motion-duration-slow");
    panel.animate(
      [
        { opacity: 0, transform: `translate(${originX}px, ${originY}px) scale(0.85)` },
        { opacity: 1, transform: "translate(0, 0) scale(1)" },
      ],
      { duration, easing: this.easing(), fill: "both" }
    );
  }

  private durationMs(token: string): number {
    const raw = getComputedStyle(this).getPropertyValue(token).trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 400;
  }

  private easing(): string {
    return getComputedStyle(this).getPropertyValue("--bfc-motion-ease").trim() || "ease-out";
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.overlay) return;
    if (e.key === "Escape") {
      // Nested-dialog semantics: one Esc cancels the most recent layer. If the confirm step is up,
      // Esc dismisses it (same as "Keep it open") rather than re-triggering close().
      if (this.overlay.querySelector(".bfc-close-confirm")) {
        this.dismissCloseConfirm();
        return;
      }
      this.close();
      return;
    }
    if (e.key !== "Tab") return;

    const focusable = Array.from(this.overlay.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const activeInShadow = this.root.activeElement as HTMLElement | null;

    if (e.shiftKey && activeInShadow === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && activeInShadow === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("bfc-discovery")) {
  customElements.define("bfc-discovery", BfcDiscoveryElement);
}
